<template>
	<TheForm :model="request" :rules="rules" ref="formRef" :waiting="waiting" :id="id">
		<ButtonSticky @buttonclick="createContact()">
			<template v-slot:icon><saveIcon /></template>
			<template v-slot:text> Speichern </template>
		</ButtonSticky>
		<section>
			<ButtonBack />

			<el-row>
				<h1>Neuer Kontakt</h1>
			</el-row>

			<TheBox :title="'Kontakt Daten'" :hasDataGrid="true">
				<el-row>
					<el-col :sm="12" :xs="24">
						<el-form-item label="Anrede:" class="datagrid--input-short" prop="gender">
							<el-select v-model="request.gender" :placeholder="'Bitte wählen'">
								<el-option v-for="option in genderOptions" :key="option.value" :label="option.label" :value="option.value" />
							</el-select>
						</el-form-item>

						<el-form-item label="Titel (vor):" prop="titleBefore">
							<el-input v-model="request.titleBefore" :placeholder="'Bitte Titel eingeben'" />
						</el-form-item>

						<el-form-item label="Titel (nach):" prop="titleAfter">
							<el-input v-model="request.titleAfter" :placeholder="'Bitte Titel eingeben'" />
						</el-form-item>

						<el-form-item label="E-Mail-Adresse:" prop="email">
							<el-input v-model="request.email" :placeholder="'Bitte Email eingeben'" />
						</el-form-item>
					</el-col>

					<el-col :sm="12" :xs="24">
						<el-form-item label="Vorname:" prop="firstName">
							<el-input v-model="request.firstName" :placeholder="'Bitte Vorname eingeben'" />
						</el-form-item>

						<el-form-item label="Nachname:" prop="lastName">
							<el-input v-model="request.lastName" :placeholder="'Bitte Nachname eingeben'" />
						</el-form-item>

						<el-form-item label="Telefon" prop="phoneNumber">
							<el-input v-model="request.phoneNumber" :placeholder="'Bitte Telefonnummer eingeben'" />
						</el-form-item>

						<el-form-item label="Firma" prop="company">
							<el-input v-model="request.company" :placeholder="'Bitte Firma eingeben'" />
						</el-form-item>
					</el-col>
				</el-row>
			</TheBox>
		</section>

		<section class="no-marginbottom">
			<el-row>
				<el-col :sm="12" :xs="24">
					<h2>Gruppenzugehörigkeit</h2>
				</el-col>
				<el-col :sm="12" :xs="24" class="flex flex--justifyend">
					<TheButton @buttonclick="openDialogNewRole = true">
						<el-icon><Plus /></el-icon>Neue Gruppenzugehörigkeit
					</TheButton>
				</el-col>
			</el-row>
		</section>

		<div v-show="userRoleError" class="el-form-item__error">{{ texts.formErrors.userRoleMissing }}</div>
		<RoleSlider :roles="request.roles ? request.roles : []" @delete="(role) => removeRoleFromUser(role)" />

		<!-- needed for form validation -->
		<el-form-item label="" prop="roles" class="is-hidden">
			<el-input v-model="request.roles" />
		</el-form-item>
	</TheForm>

	<DialogNewRole v-model:open="openDialogNewRole" @addNewRole="(role) => addRoleToUser(role)" :roleOptions="availableRoles" />
	<DialogLeavePage v-model:open="openClosingDialog" @cancel="leavePage" @save="createContact(true)" />
</template>

<script lang="ts">
import CasesTable from '@/components/tables/CasesTable.vue';
import RoleSlider from '../components/slider/RoleSlider.vue';

import { getDecision } from '@/requests/decisions';
import { Delete, Plus, Loading } from '@element-plus/icons-vue';
import { defineComponent, ref, markRaw } from 'vue';
import BoxRole from '@/components/boxes/BoxRole.vue';
import DialogNewRole from '@/components/dialogs/DialogNewRole.vue';
import InputLite from '@/components/forms/InputLite.vue';
import { register } from '@/requests/auth';
import { categories } from '@/utils/categories';
import { returnFormOptions } from '@/utils/formOptions';
import { ElMessage, FormInstance } from 'element-plus';
import saveIcon from '../assets/icons/saveIcon.vue';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import DialogLeavePage from '../components/dialogs/DialogLeavePage.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import { RegisterRequest } from '../types/types';
import { texts } from '../utils/texts';
import { ErrorCodes, getErrorCode, getErrorMessage } from '@/utils/errorHandling';

import { validateForm, hasFormChanged, resetForm } from '@/utils/functions';

const ElMessageDuration = 3000;

const initialRequestObj = {
	email: '',
	firstName: '',
	lastName: '',
	gender: null,
	postalCode: '',
	city: '',
	street: '',
	phoneNumber: '',
	company: '',
	titleBefore: '',
	titleAfter: '',
	initials: '',
	roles: [] as any,
} as RegisterRequest;

export default defineComponent({
	name: 'KontaktNeuView',
	data() {
		return {
			request: initialRequestObj,
			initialData: JSON.parse(JSON.stringify(initialRequestObj)), // deep copy to ensure different objects
			waiting: false,
			loading: true,
			rules: {
				gender: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'change' }],
				email: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' },
					{ type: 'email', message: texts.formErrors.emailReq, trigger: ['blur'] },
				],
				firstName: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				lastName: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
			},
			userRoleError: false,
			id: 'contactForm',
			categories,
			openDialogNewRole: false,
			openDeleteContactModal: false,
			openClosingDialog: false,
			checkPageLeave: true,
			pageLeaveTo: null,
			DialogNewRole,
		};
	},
	components: {
		ButtonBack,
		ButtonSticky,
		TheButton,
		TheForm,
		DialogLeavePage,
		TheBox,
		CasesTable,
		Plus,
		Delete,
		saveIcon,
		BoxRole,
		DialogNewRole,
		InputLite,
		RoleSlider,
		Loading,
	},
	setup() {
		const formRef = ref<FormInstance>();

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
		genderOptions() {
			return returnFormOptions('gender');
		},
		availableRoles() {
			// @ts-ignore
			return returnFormOptions('contactRole').filter((role) => !this.request.roles.some((userRole) => userRole.name === role.value?.name));
		},
	},
	methods: {
		async createContact(leavePage = false) {
			this.openClosingDialog = false;

			this.userRoleError = false;

			let isValid = await validateForm(this.formRef, this.id);
			if (this.request.roles.length === 0) {
				isValid = false;
				this.userRoleError = true;
			}

			if (!isValid) return;

			this.waiting = true;
			const waitingMsg = ElMessage({ message: texts.formErrors.waiting, type: 'info', duration: 0, showClose: false, icon: markRaw(Loading) });

			const registerContactResult = await register(this.request);
			if (registerContactResult.success) {
				if (leavePage) this.leavePage();
				else {
					this.checkPageLeave = false;
					this.$router.push({ name: 'KontaktDetail', params: { id: registerContactResult.data.id }, replace: true });
				}
			} else {
				// TODO OEWR-91: Fallback (Vergleich mit der englischen Meldung) nach Prod-Release entfernen
				const emailExists =
					getErrorCode(registerContactResult.error) === ErrorCodes.EmailAlreadyExists ||
					getErrorMessage(registerContactResult.error, '') === 'A user with this email address already exists.';
				if (emailExists) {
					ElMessage({ message: texts.user.emailExistsContact, type: 'error', duration: ElMessageDuration });
				} else {
					ElMessage({ message: texts.user.createContactFailed, type: 'error', duration: ElMessageDuration });
				}
			}

			this.waiting = false;
			waitingMsg.close();
		},
		addRoleToUser(role) {
			this.request.roles.push({
				name: role.value.name,
			});
			this.userRoleError = false;
		},
		removeRoleFromUser(role) {
			this.request.roles = this.request.roles.filter((r) => r.name !== role.name);
		},
		leavePage() {
			this.openClosingDialog = false;
			this.checkPageLeave = false;
			// @ts-ignore
			this.$router.push(this.pageLeaveTo);
		},
		handleBeforeRouteLeave(to, from, next) {
			if (this.checkPageLeave === false) {
				resetForm(this.formRef, this.id);
				next();
				return;
			}

			// check if request data has changed
			const hasChanged = hasFormChanged({ initialData: this.initialData, currentData: this.request });

			if (!hasChanged) {
				next();
				return;
			}

			// ABORT NAVIGATION & SHOW DIALOG
			this.openClosingDialog = true;
			this.pageLeaveTo = to;
			next(false);
		},
	},
	beforeRouteLeave(to, from, next) {
		this.handleBeforeRouteLeave(to, from, next);
	},
});
</script>
